/**
 * complianceSeed — REG-AUTO-1.
 *
 * Seed records for regulatory_feeds, regulatory_exposures, and
 * compliance_filings subcollections — kairos-demo tenant.
 *
 * Written to localStorage as subcollection keys so DataService.querySub
 * can read them in demo/offline mode.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const T = DEMO_TENANT_ID;
const now = new Date().toISOString();
const future = (d) => new Date(Date.now() + d * 86400000).toISOString();
const past = (d) => new Date(Date.now() - d * 86400000).toISOString();

// ── Regulatory feed items (3 required by spec) ────────────────────────────────

export const SEED_REGULATORY_FEED_ITEMS = [
  {
    id: 'reg-feed-001',
    tenantId: T,
    source: 'ustr',
    title: 'Section 301 List 3 Rate Increase — Electronic Products',
    summary: 'USTR announces rate increase from 25% to 30% for List 3 goods. Applies to laptops, tablets, and portable computing devices imported from covered countries. Importers should review open POs and calculate additional duty exposure before effective date.',
    affectedHtsCodes: ['8471.30', '8471.41', '8471.49'],
    effectiveDate: future(30),
    severity: 'high',
    rawUrl: 'https://ustr.gov/issue-areas/enforcement/section-301-investigations',
    ingestedAt: past(1),
    ingestedDate: past(1).slice(0, 10),
    tariffRateDelta: 5,
    isDeleted: false,
    createdAt: past(1),
    updatedAt: past(1),
    version: 1,
    source_system: 'reg_auto_1',
  },
  {
    id: 'reg-feed-002',
    tenantId: T,
    source: 'cbp',
    title: 'HTS Reclassification — Industrial Flooring Materials',
    summary: 'CBP Informed Compliance Publication clarifies classification of vinyl and composite industrial flooring under Chapter 39. Products previously classified under 3918.10 may require reclassification with potential duty impact.',
    affectedHtsCodes: ['3918.10', '3918.90'],
    effectiveDate: future(14),
    severity: 'medium',
    rawUrl: 'https://www.cbp.gov/trade/rulings/informed-compliance-publications',
    ingestedAt: past(2),
    ingestedDate: past(2).slice(0, 10),
    tariffRateDelta: 3.5,
    isDeleted: false,
    createdAt: past(2),
    updatedAt: past(2),
    version: 1,
    source_system: 'reg_auto_1',
  },
  {
    id: 'reg-feed-003',
    tenantId: T,
    source: 'ofac',
    title: 'SDN List Update — 3 new entries',
    summary: 'OFAC Specially Designated Nationals and Blocked Persons List updated with 3 new entity additions. Immediate action required: screen all active carriers, suppliers, and counterparties against updated list before next shipment.',
    affectedHtsCodes: [],
    effectiveDate: now,
    severity: 'critical',
    rawUrl: 'https://www.treasury.gov/ofac/downloads/sdnlist.txt',
    ingestedAt: now,
    ingestedDate: now.slice(0, 10),
    tariffRateDelta: 0,
    isDeleted: false,
    createdAt: now,
    updatedAt: now,
    version: 1,
    source_system: 'reg_auto_1',
  },
];

// ── Exposure records (2 linked to demo POs) ───────────────────────────────────

export const SEED_REGULATORY_EXPOSURES = [
  {
    id: 'reg-exp-001',
    tenantId: T,
    regulatoryItemId: 'reg-feed-001',
    affectedPoIds: ['po-battery-001', 'po-solar-002'],
    affectedLoadIds: ['load-00003'],
    totalExposure: 18750,
    currency: 'USD',
    calculatedAt: past(0),
    htsCodes: ['8471.30', '8471.41'],
    tariffRateDelta: 5,
    severity: 'high',
    source: 'ustr',
    mitigation: null,
    isDeleted: false,
    createdAt: past(0),
    updatedAt: past(0),
    version: 1,
    source_system: 'reg_auto_1',
  },
  {
    id: 'reg-exp-002',
    tenantId: T,
    regulatoryItemId: 'reg-feed-002',
    affectedPoIds: ['po-flooring-003'],
    affectedLoadIds: [],
    totalExposure: 4200,
    currency: 'USD',
    calculatedAt: past(1),
    htsCodes: ['3918.10'],
    tariffRateDelta: 3.5,
    severity: 'medium',
    source: 'cbp',
    mitigation: null,
    isDeleted: false,
    createdAt: past(1),
    updatedAt: past(1),
    version: 1,
    source_system: 'reg_auto_1',
  },
];

// ── Compliance filings (1 in pending_review) ──────────────────────────────────

export const SEED_COMPLIANCE_FILINGS = [
  {
    id: 'filing-001',
    tenantId: T,
    filingType: 'cbp_post_summary_correction',
    status: 'pending_review',
    confidence: 0.87,
    autoSubmit: false,
    generatedAt: past(0),
    generatedBy: 'aegis.compliance',
    reviewedBy: null,
    reviewedAt: null,
    submittedAt: null,
    humanReadableSummary: [
      'CBP Post-Summary Correction',
      'Entry: N/A',
      'HTS Reclassification: 8471.30 → 8471.41',
      'Quantity: 500 units',
      'Declared Value: $125,000 USD',
      'Duty Delta: +$6,250 USD',
      'Reason: USTR Section 301 List 3 rate increase — reclassification required per CBP guidance',
    ].join('\n'),
    payload: {
      entryNumber: null,
      htsOld: '8471.30',
      htsNew: '8471.41',
      quantity: 500,
      value: 125000,
      dutyDelta: 6250,
      reason: 'USTR Section 301 List 3 rate increase — reclassification required per CBP guidance',
      supportingDocuments: [],
    },
    isDeleted: false,
    createdAt: past(0),
    updatedAt: past(0),
    version: 1,
    source_system: 'reg_auto_1',
  },
  {
    id: 'filing-002',
    tenantId: T,
    filingType: 'ofac_screening_record',
    status: 'submitted',
    confidence: 0.99,
    autoSubmit: true,
    generatedAt: past(2),
    generatedBy: 'aegis.compliance',
    reviewedBy: null,
    reviewedAt: null,
    submittedAt: past(2),
    humanReadableSummary: [
      'OFAC SDN Screening Record',
      'Entity: Swift Freight Solutions LLC',
      'Entity Type: carrier',
      'Screen Date: ' + past(2).slice(0, 10),
      'SDN List Version: current',
      'Result: CLEAR — No match found',
    ].join('\n'),
    payload: {
      entityName: 'Swift Freight Solutions LLC',
      entityType: 'carrier',
      entityId: 'carrier-swft',
      screenDate: past(2),
      sdnListVersion: 'current',
      result: 'clear',
      matchDetails: null,
      screenedBy: 'aegis.compliance',
    },
    isDeleted: false,
    createdAt: past(2),
    updatedAt: past(2),
    version: 1,
    source_system: 'reg_auto_1',
  },
];

// ── Seed function ─────────────────────────────────────────────────────────────

export function seedComplianceData() {
  const feedKey = `wc_sub_regulatory_feeds__${T}__items`;
  const exposureKey = `wc_sub_regulatory_exposures__${T}__exposures`;
  const filingsKey = `wc_sub_compliance_filings__${T}__filings`;

  if (!localStorage.getItem(feedKey)) {
    localStorage.setItem(feedKey, JSON.stringify(SEED_REGULATORY_FEED_ITEMS));
  }
  if (!localStorage.getItem(exposureKey)) {
    localStorage.setItem(exposureKey, JSON.stringify(SEED_REGULATORY_EXPOSURES));
  }
  if (!localStorage.getItem(filingsKey)) {
    localStorage.setItem(filingsKey, JSON.stringify(SEED_COMPLIANCE_FILINGS));
  }
}
